import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/lib/supabase";

interface ActivityEvent {
  id: number;
  event_type: string;
  event_data: Record<string, unknown> | null;
  created_at: string;
}

type EventType = "all" | "search" | "profile_view" | "banner_interest" | "banner_submit" | "registration";

const EVENT_LABELS: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  search: { label: "Busca", icon: "ri-search-line", color: "text-blue-700", bg: "bg-blue-50" },
  profile_view: { label: "Visualização de perfil", icon: "ri-user-line", color: "text-primary", bg: "bg-primary/10" },
  banner_interest: { label: "Interesse em banner", icon: "ri-flag-line", color: "text-green-700", bg: "bg-green-50" },
  banner_submit: { label: "Envio de anúncio", icon: "ri-image-line", color: "text-purple-700", bg: "bg-purple-50" },
  registration: { label: "Cadastro", icon: "ri-user-add-line", color: "text-accent-700", bg: "bg-accent-100" },
  member_merge: { label: "Mesclagem de cadastros", icon: "ri-git-merge-line", color: "text-amber-700", bg: "bg-amber-50" },
  member_unmerge: { label: "Mesclagem desfeita", icon: "ri-arrow-go-back-line", color: "text-orange-700", bg: "bg-orange-50" },
};

export default function ActivityLogManager() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<EventType>("all");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [dateRange, setDateRange] = useState<"today" | "7days" | "30days" | "all">("7days");

  useEffect(() => {
    async function load() {
      setLoading(true);

      let query = supabase
        .from("activity_log")
        .select("*")
        .order("created_at", { ascending: false });

      if (dateRange === "today") {
        const today = new Date().toISOString().split("T")[0];
        query = query.gte("created_at", `${today}T00:00:00Z`);
      } else if (dateRange === "7days") {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        query = query.gte("created_at", sevenDaysAgo);
      } else if (dateRange === "30days") {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        query = query.gte("created_at", thirtyDaysAgo);
      }

      const { data, error } = await query;

      if (!error && data) {
        setEvents(data as ActivityEvent[]);
      }
      setLoading(false);
    }
    load();
  }, [dateRange]);

  const filteredEvents = useMemo(() => {
    if (filter === "all") return events;
    return events.filter((e) => e.event_type === filter);
  }, [events, filter]);

  const stats = useMemo(() => {
    const total = events.length;
    const searches = events.filter((e) => e.event_type === "search").length;
    const profileViews = events.filter((e) => e.event_type === "profile_view").length;
    const bannerInterests = events.filter((e) => e.event_type === "banner_interest").length;
    const bannerSubmits = events.filter((e) => e.event_type === "banner_submit").length;
    const registrations = events.filter((e) => e.event_type === "registration").length;
    return { total, searches, profileViews, bannerInterests, bannerSubmits, registrations };
  }, [events]);

  const uniqueSearches = useMemo(() => {
    const terms = new Set<string>();
    events
      .filter((e) => e.event_type === "search")
      .forEach((e) => {
        const q = (e.event_data?.query as string) || "";
        if (q) terms.add(q.toLowerCase());
      });
    return terms;
  }, [events]);

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatEventData = (type: string, data: Record<string, unknown> | null) => {
    if (!data) return null;

    switch (type) {
      case "search":
        return (
          <span className="font-body text-sm text-brown">
            Termo: <strong className="text-primary">{(data.query as string) || "—"}</strong>
          </span>
        );
      case "profile_view":
        return (
          <span className="font-body text-sm text-brown">
            Perfil: <strong>{(data.profile_name as string) || `ID ${data.profile_id || "—"}`}</strong>
            {data.profile_profession && (
              <span className="text-brown/60"> — {(data.profile_profession as string)}</span>
            )}
          </span>
        );
      case "banner_interest":
        return (
          <span className="font-body text-sm text-brown">
            Banner: <strong>{(data.banner_title as string) || `ID ${data.banner_id || "—"}`}</strong>
            {data.banner_category && (
              <span className="text-brown/60"> — {(data.banner_category as string)}</span>
            )}
            {data.member_name && (
              <span className="text-brown/60 block text-xs mt-0.5">Por: {(data.member_name as string)}</span>
            )}
          </span>
        );
      case "banner_submit":
        return (
          <span className="font-body text-sm text-brown">
            Anúncio: <strong>{(data.category as string) || "—"}</strong>
            {data.title && <span> — {(data.title as string)}</span>}
            {data.advertiser_name && (
              <span className="text-brown/60 block text-xs mt-0.5">Por: {(data.advertiser_name as string)}</span>
            )}
          </span>
        );
      case "registration":
        return (
          <span className="font-body text-sm text-brown">
            Nova integrante: <strong>{(data.full_name as string) || "—"}</strong>
            {data.profession && <span className="text-brown/60"> — {(data.profession as string)}</span>}
            {data.city_state && <span className="text-brown/60"> — {(data.city_state as string)}</span>}
          </span>
        );
      case "member_merge":
        return (
          <span className="font-body text-sm text-brown">
            Mantido: <strong>{(data.kept_member_name as string) || "—"}</strong>
            {(data.merged_member_names as string[])?.length ? (
              <span className="text-brown/60"> — mesclou {(data.merged_member_names as string[]).join(", ")}</span>
            ) : null}
          </span>
        );
      case "member_unmerge":
        return (
          <span className="font-body text-sm text-brown">
            Mesclagem desfeita: <strong>{(data.member_name as string) || "—"}</strong>
          </span>
        );
      default:
        return (
          <span className="font-body text-xs text-brown/60 font-mono">
            {JSON.stringify(data)}
          </span>
        );
    }
  };

  const filterButtons: { key: EventType; label: string; count: number }[] = [
    { key: "all", label: "Todos", count: stats.total },
    { key: "search", label: "Buscas", count: stats.searches },
    { key: "profile_view", label: "Perfis vistos", count: stats.profileViews },
    { key: "banner_interest", label: "Interesses", count: stats.bannerInterests },
    { key: "banner_submit", label: "Anúncios", count: stats.bannerSubmits },
    { key: "registration", label: "Cadastros", count: stats.registrations },
  ];

  return (
    <div>
      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <p className="font-display text-2xl text-brown">{stats.total}</p>
          <p className="font-body text-xs text-brown/50 uppercase tracking-wide">Total</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 text-center">
          <p className="font-display text-2xl text-blue-700">{stats.searches}</p>
          <p className="font-body text-xs text-brown/50 uppercase tracking-wide">Buscas</p>
        </div>
        <div className="bg-primary/10 rounded-xl p-4 text-center">
          <p className="font-display text-2xl text-primary">{stats.profileViews}</p>
          <p className="font-body text-xs text-brown/50 uppercase tracking-wide">Perfis vistos</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <p className="font-display text-2xl text-green-700">{stats.bannerInterests}</p>
          <p className="font-body text-xs text-brown/50 uppercase tracking-wide">Interesses</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 text-center">
          <p className="font-display text-2xl text-purple-700">{stats.bannerSubmits}</p>
          <p className="font-body text-xs text-brown/50 uppercase tracking-wide">Anúncios</p>
        </div>
        <div className="bg-accent-100 rounded-xl p-4 text-center">
          <p className="font-display text-2xl text-accent-700">{stats.registrations}</p>
          <p className="font-body text-xs text-brown/50 uppercase tracking-wide">Cadastros</p>
        </div>
      </div>

      {/* Date range + search terms */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          {([
            { key: "today" as const, label: "Hoje" },
            { key: "7days" as const, label: "7 dias" },
            { key: "30days" as const, label: "30 dias" },
            { key: "all" as const, label: "Tudo" },
          ]).map((opt) => (
            <button
              key={opt.key}
              onClick={() => setDateRange(opt.key)}
              className={`px-4 py-2 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                dateRange === opt.key
                  ? "bg-primary text-white shadow-sm"
                  : "bg-white text-brown hover:bg-beige border border-nude/60"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {uniqueSearches.size > 0 && (
          <p className="font-body text-xs text-brown/50">
            <i className="ri-search-line mr-1" />
            {uniqueSearches.size} termo{uniqueSearches.size > 1 ? "s" : ""} de busca único{uniqueSearches.size > 1 ? "s" : ""}
          </p>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {filterButtons.map((btn) => (
          <button
            key={btn.key}
            onClick={() => setFilter(btn.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
              filter === btn.key
                ? "bg-brown text-white shadow-sm"
                : "bg-white text-brown hover:bg-beige border border-nude/60"
            }`}
          >
            <span>{btn.label}</span>
            <span className={`font-display text-base ${filter === btn.key ? "text-white/70" : "text-brown/40"}`}>
              {btn.count}
            </span>
          </button>
        ))}
      </div>

      {/* Events list */}
      {loading ? (
        <div className="text-center py-16">
          <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-sm text-brown/60">Carregando atividades...</p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center">
          <i className="ri-bar-chart-line text-4xl text-brown/20 mb-4" />
          <p className="font-body text-brown/60">Nenhuma atividade registrada neste período.</p>
          <p className="font-body text-xs text-brown/40 mt-2">
            As atividades aparecem quando usuárias buscam, visitam perfis, clicam em banners ou se cadastram.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredEvents.map((event) => {
            const meta = EVENT_LABELS[event.event_type] || {
              label: event.event_type,
              icon: "ri-file-list-line",
              color: "text-brown",
              bg: "bg-beige",
            };
            const isExpanded = expandedIds.has(event.id);

            return (
              <div
                key={event.id}
                className="bg-white rounded-xl p-4 md:p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full shrink-0 ${meta.bg}`}>
                    <i className={`${meta.icon} ${meta.color} text-sm`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`font-body text-xs font-medium uppercase tracking-wide ${meta.color}`}>
                        {meta.label}
                      </span>
                      <span className="font-body text-xs text-brown/30">
                        {formatDate(event.created_at)}
                      </span>
                    </div>
                    <div className="mb-1">
                      {formatEventData(event.event_type, event.event_data)}
                    </div>

                    {/* Raw JSON toggle */}
                    {event.event_data && Object.keys(event.event_data).length > 0 && (
                      <button
                        onClick={() => toggleExpand(event.id)}
                        className="flex items-center gap-1 font-body text-xs text-brown/40 hover:text-primary transition-colors cursor-pointer mt-1"
                      >
                        <i className={isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                        {isExpanded ? "Ocultar detalhes" : "Ver detalhes completos"}
                      </button>
                    )}

                    {isExpanded && event.event_data && (
                      <div className="mt-2 p-3 rounded-lg bg-beige/50 font-mono text-xs text-brown/60 overflow-x-auto">
                        <pre className="whitespace-pre-wrap break-all">
                          {JSON.stringify(event.event_data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}